import * as TabsPrimitive from "@radix-ui/react-tabs";
import { ChipLabel } from "@rosc/ui/v32/chip";
import { History, Languages, RefreshCcw, RotateCcw } from "lucide-react";
import type { JSX, ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { AUDIO_TRANSCRIPTION_NO_REALTIME_PROVIDERS } from "@/common/config";
import type { NoteTemplate } from "@/common/types/notesTemplates";
import { Button } from "@/components/ui/button";
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ParagraphSkeleton } from "@/components/ui/skeleton/ParagraphSkeleton";
import { V3Button } from "@/components/ui/v3/button";
import { v3ChipVariants } from "@/components/ui/v3/chip";
import { toast } from "@/hooks/use-toast";
import { useNoteRestore } from "@/hooks/visits/useNoteRestore";
import {
  reviewTextKey,
  useTranscriptFlags,
} from "@/hooks/visits/useVisitReview";
import { useVisitReads } from "@/hooks/visits/VisitReads";
import { documentTemplateName } from "@/lib/document-name";
import { noteOptionLabel } from "@/lib/note-options";
import { hasMainRecording, isPreChartDraft } from "@/lib/pre-chart";
import type { ReadingLevel } from "@/lib/reading-level";
import {
  detectSummaryLanguage,
  type SummaryLanguage,
} from "@/lib/summary-language";
import { cn, getVisitTitle } from "@/lib/utils";
import { countVerifyMarkers } from "@/lib/verifyMarkers";
import type { FileData } from "@/services/files";
import {
  type MainNoteActions,
  MainNoteNotices,
  MainNoteTools,
  useMainNoteActions,
} from "../components/MainNoteTools";
import {
  isNoteGenerating,
  NoteGeneratingBody,
  useNoteGenerationStream,
} from "../components/NoteGenerating";
import { SectionedNote } from "../components/SectionedNote";
import { useVisitNoteSave } from "../components/useVisitNoteSave";
import {
  FileLatestVersion,
  VisitVersionSelect,
} from "../components/VisitVersionSelect";
import {
  NOTE_READING_CLASS,
  NoteDocument,
  type VerifyReviewRequest,
} from "./NoteDocument";
import { NoteExportMenu } from "./NoteExportMenu";
import { ReadingLevelDialog } from "./ReadingLevelDialog";
import type { AssistantUnavailableReason } from "./WorkspaceAssistant";

export const MAIN_DOCUMENT_KEY = "main";
export const noteDocumentKey = (
  note: Pick<NoteTemplate, "public_id">,
): string => `note-${note.public_id}`;

export interface DocumentState {
  editing: boolean;
  unavailable: AssistantUnavailableReason | null;
}

interface DocumentProps {
  heading?: React.ReactNode;
  // Document name, for exports.
  name?: string;
  onEditingChange: (editing: boolean) => void;
  onOpenAssistant?: () => void;
}

const DocumentHeading = ({
  name,
  meta,
  badge,
}: {
  name: string;
  meta: string;
  // A small label after the name (a patient summary's language).
  badge?: ReactNode;
}): JSX.Element =>
  AppConfig.enableAppRedesign ? (
    <div className="font-v3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <h2 className="text-[17px] font-semibold text-v3-text">{name}</h2>
        {badge}
      </div>
      {meta && <p className="mt-0.5 text-[13px] text-v3-text-3">{meta}</p>}
    </div>
  ) : (
    <>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <h2 className="text-[17px] font-semibold leading-[22px] sm:text-xl">
          {name}
        </h2>
        {badge}
      </div>
      {meta && <p className="mt-1 text-sm text-muted-foreground">{meta}</p>}
    </>
  );

// The language a patient summary is written in, as a small label, so it is
// clear before it is handed to the patient. Read from the text; none when
// unsure.
export const SummaryLanguageLabel = ({
  note,
}: {
  note: Pick<NoteTemplate, "template" | "result">;
}): JSX.Element | null => {
  const { t } = useTranslation();
  if (note.template !== PATIENT_SUMMARY_TEMPLATE) return null;
  const language = detectSummaryLanguage(note.result);
  if (!language) return null;
  const label = t(`visit.workspace.summaryLanguage.languages.${language}`);
  return (
    <ChipLabel
      aria-label={t("visit.workspace.summaryLanguage.writtenIn", {
        language: label,
      })}
      data-testid="summary-language-label"
    >
      <Languages aria-hidden />
      {label}
    </ChipLabel>
  );
};

const documentMeta = (
  doc: Pick<FileData, "style" | "format" | "exam">,
): string =>
  [doc.style, doc.format, doc.exam]
    .filter((value): value is string => Boolean(value))
    .map(noteOptionLabel)
    .join(" · ");

// Export (PDF, Word) of the document as shown: the visit title and date, the
// document name and options, and its text.
const ExportAction = ({
  file,
  name,
  doc,
  result,
  version,
  asMenuItems,
}: {
  file: FileData;
  name: string;
  doc: Pick<FileData, "style" | "format" | "exam">;
  result: string | null | undefined;
  version?: string;
  // The visit's "…" menu on phones (see VisitTopBar).
  asMenuItems?: boolean;
}): JSX.Element => {
  return (
    <NoteExportMenu
      asMenuItems={asMenuItems}
      document={{
        title: getVisitTitle(file.title, file.rowno),
        documentName: name,
        details: [
          ...[doc.style, doc.format, doc.exam].map((value) =>
            value ? noteOptionLabel(value) : value,
          ),
          ...(version ? [version] : []),
        ],
        visitDate: file.createDate,
        markdown: result ?? "",
      }}
    />
  );
};

// Why the assistant cannot edit the open document right now, if it cannot.
export const assistantUnavailableFor = ({
  result,
  editing,
  generating = false,
  oldVersion = false,
}: {
  result: string | null | undefined;
  editing: boolean;
  generating?: boolean;
  oldVersion?: boolean;
}): AssistantUnavailableReason | null => {
  if (generating) return "generating";
  if (oldVersion) return "old-version";
  if (editing) return "editing";
  if (!result?.trim()) return "no-text";
  return null;
};

// Restore in the version history: the version on screen becomes the latest
// note, as a new version (the note it replaces stays in the history).
const useRestoreShownVersion = (file: FileData) => {
  const { t } = useTranslation();
  const { fileVersion, setVersionId } = useVisitReads();
  const restoreNote = useNoteRestore();
  const [restoring, setRestoring] = useState(false);
  const text = fileVersion?.result;
  const restore = async (): Promise<void> => {
    if (!text?.trim() || restoring) return;
    setRestoring(true);
    const outcome = await restoreNote({
      visitPublicId: file.public_id,
      notePublicId: null,
      restore: text,
      expected: file.result ?? "",
    });
    setRestoring(false);
    if (outcome === "restored") {
      setVersionId(FileLatestVersion);
      toast({
        variant: "success",
        title: t("visit.workspace.versionRestored", {
          version: fileVersion?.version ?? "",
        }),
      });
    } else
      toast({
        variant: "error",
        title:
          outcome === "stale"
            ? t("visit.workspace.restoreStale")
            : t("visit.workspace.restoreError"),
      });
  };
  return { canRestore: !!text?.trim(), restoring, restore };
};

// Phones: the main note's version picker and Regenerate as "…" menu items.
// Same calls as the toolbar controls they stand in for.
const MainNoteMenuItems = ({
  file,
  actions,
  viewingOldVersion,
  restore,
}: {
  file: FileData;
  actions: MainNoteActions;
  viewingOldVersion: boolean;
  restore?: ReturnType<typeof useRestoreShownVersion>;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const { versionLoading, selectedVersion, setVersionId } = useVisitReads();
  const {
    loading: regenerating,
    recovery,
    savedProvider,
  } = actions.regeneration;
  const showVersions = file.versions.length > 0;
  const showRegenerate = !viewingOldVersion && actions.canRegenerate;
  // Only worth a choice when there is more than one provider.
  const showProviders =
    showRegenerate && AUDIO_TRANSCRIPTION_NO_REALTIME_PROVIDERS.length > 1;
  if (!showVersions && !showRegenerate) return null;

  return (
    <>
      {showRegenerate && (
        <DropdownMenuItem
          className="gap-3"
          disabled={actions.busy || recovery}
          onSelect={actions.regenerate}
        >
          <RefreshCcw
            aria-hidden
            className={cn(regenerating && "animate-spin")}
          />
          {t("visits.templateViewer.buttons.regenerate")}
        </DropdownMenuItem>
      )}
      {showProviders && (
        <>
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
            {t("visit.workspace.transcriptionProvider")}
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={savedProvider ?? actions.providerSelected}
            onValueChange={actions.setProviderSelected}
          >
            {AUDIO_TRANSCRIPTION_NO_REALTIME_PROVIDERS.map((option) => (
              <DropdownMenuRadioItem
                key={option.value}
                disabled={actions.busy || recovery}
                value={option.value}
              >
                {option.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </>
      )}
      {showVersions && (
        <>
          {showRegenerate && <DropdownMenuSeparator />}
          <DropdownMenuLabel className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <History aria-hidden className="size-4" />
            {t("visit.workspace.versionHistory")}
          </DropdownMenuLabel>
          {viewingOldVersion && restore && (
            <DropdownMenuItem
              className="gap-3"
              data-testid="menu-restore-version"
              disabled={!restore.canRestore || restore.restoring}
              onSelect={() => void restore.restore()}
            >
              <RotateCcw aria-hidden />
              {t("visit.workspace.restoreVersion")}
            </DropdownMenuItem>
          )}
          <DropdownMenuRadioGroup
            value={selectedVersion}
            onValueChange={setVersionId}
          >
            <DropdownMenuRadioItem
              disabled={versionLoading}
              value={FileLatestVersion}
            >
              {t("visits.templateViewer.version.latest")}
            </DropdownMenuRadioItem>
            {file.versions.map((version) => (
              <DropdownMenuRadioItem
                key={version.public_id}
                disabled={versionLoading}
                value={version.public_id.toString()}
              >
                {t("visits.templateViewer.version.version")} {version.version}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </>
      )}
    </>
  );
};

// A red allergy flag from the note safety check (VITE_TRANSCRIPT_FLAGS_ENABLED)
// changes the copy/export gate wording: the plan may include an allergen,
// not "unclear values". Read only while the note has values to review; off,
// nothing is read (and no query client is needed). The flag setting never
// changes at runtime, so the note below keeps its state.
const WithAllergyFlag = ({
  file,
  children,
}: {
  file: FileData;
  children: (allergyFlagged: boolean) => JSX.Element;
}): JSX.Element =>
  AppConfig.enableTranscriptFlags ? (
    <ReadAllergyFlag file={file}>{children}</ReadAllergyFlag>
  ) : (
    children(false)
  );

const ReadAllergyFlag = ({
  file,
  children,
}: {
  file: FileData;
  children: (allergyFlagged: boolean) => JSX.Element;
}): JSX.Element => {
  const { data } = useTranscriptFlags(
    file.public_id,
    `gate|${file.status}|${reviewTextKey(file.result)}`,
    countVerifyMarkers(file.result) > 0,
  );
  return children(
    !!data?.transcripts.some((transcript) =>
      transcript.flags.some(
        (flag) => flag.category === "allergy" && flag.severity === "red",
      ),
    ),
  );
};

// The main note's review step, asked for by Create (patient summary,
// referral letter) while the note has unresolved "[verify]" values.
interface MainReviewProps {
  reviewRequest?: VerifyReviewRequest | null;
  onReviewRequestTaken?: () => void;
  // v3.2: "Copy to chart" in the visit header copies the main note.
  copyInHeader?: boolean;
}

const MainDocumentReady = ({
  file,
  heading,
  name,
  onEditingChange,
  onOpenAssistant,
  reviewRequest,
  onReviewRequestTaken,
  copyInHeader,
}: DocumentProps & MainReviewProps & { file: FileData }): JSX.Element => {
  const { t } = useTranslation();
  const actions = useMainNoteActions(file);
  const {
    fileVersion,
    versionLoading,
    versionError,
    selectedVersion,
    setVersionId,
  } = useVisitReads();
  const viewingOldVersion = selectedVersion !== FileLatestVersion;
  const restore = useRestoreShownVersion(file);
  const menuItems = (
    <MainNoteMenuItems
      actions={actions}
      file={file}
      restore={restore}
      viewingOldVersion={viewingOldVersion}
    />
  );
  const restoreLabel = t("visit.workspace.restoreVersion");
  const notices = viewingOldVersion ? null : (
    <MainNoteNotices actions={actions} />
  );

  const tools = (
    <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-start sm:gap-2">
      {file.versions.length > 0 && (
        <div className="flex items-center gap-2">
          <History aria-hidden className="size-4 text-muted-foreground" />
          <VisitVersionSelect className="h-11 lg:h-9" file={file} />
        </div>
      )}
      {!viewingOldVersion && (
        <MainNoteTools actions={actions} variant="workspace" />
      )}
    </div>
  );

  if (viewingOldVersion) {
    return (
      <NoteDocument
        banner={
          <div
            className={cn(
              "flex flex-wrap items-center justify-between gap-2 text-sm",
              AppConfig.enableAppRedesign
                ? "mb-5 rounded-[14px] bg-v3-surface-2 py-2 pl-4 pr-2 font-v3 text-v3-text"
                : "border-b bg-muted/40 px-4 py-2 sm:px-6",
            )}
            role="status"
          >
            {t("visit.workspace.viewingVersion", {
              version: fileVersion?.version ?? "",
            })}
            {AppConfig.enableAppRedesign ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <V3Button
                  data-testid="restore-version"
                  disabled={!restore.canRestore || restore.restoring}
                  variant="ghost"
                  onClick={() => void restore.restore()}
                >
                  <RotateCcw aria-hidden />
                  {restoreLabel}
                </V3Button>
                <V3Button
                  variant="secondary"
                  onClick={() => {
                    setVersionId(FileLatestVersion);
                  }}
                >
                  {t("visit.workspace.backToLatest")}
                </V3Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  className="h-11 lg:h-8"
                  data-testid="restore-version"
                  disabled={!restore.canRestore || restore.restoring}
                  size="sm"
                  type="button"
                  variant="ghost"
                  onClick={() => void restore.restore()}
                >
                  <RotateCcw aria-hidden />
                  {restoreLabel}
                </Button>
                <Button
                  className="h-11 lg:h-8"
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setVersionId(FileLatestVersion);
                  }}
                >
                  {t("visit.workspace.backToLatest")}
                </Button>
              </div>
            )}
          </div>
        }
        copyInHeader={copyInHeader}
        editable={false}
        exportAction={
          !versionLoading &&
          !versionError && (
            <ExportAction
              doc={file}
              file={file}
              name={name ?? documentTemplateName(file) ?? ""}
              result={fileVersion?.result}
              version={t("visit.workspace.export.version", {
                version: fileVersion?.version ?? "",
              })}
            />
          )
        }
        heading={heading}
        menuItems={menuItems}
        result={fileVersion?.result}
        tools={tools}
        onEditingChange={onEditingChange}
      >
        {versionLoading ? (
          <ParagraphSkeleton lines={10} />
        ) : versionError ? (
          <p role="alert">{t("error.unknown")}</p>
        ) : (
          <SectionedNote result={fileVersion?.result || ""} />
        )}
      </NoteDocument>
    );
  }

  return (
    <WithAllergyFlag file={file}>
      {(allergyFlagged) => (
        <NoteDocument
          busy={actions.busy}
          copyInHeader={copyInHeader}
          exportAction={
            <ExportAction
              doc={file}
              file={file}
              name={name ?? documentTemplateName(file) ?? ""}
              result={file.result}
            />
          }
          allergyFlagged={allergyFlagged}
          feedbackVisitPublicId={file.public_id}
          heading={heading}
          menuItems={menuItems}
          notices={notices}
          result={file.result}
          reviewRequest={reviewRequest}
          saving={actions.saving}
          reviewCardWording={
            isPreChartDraft(file.status)
              ? {
                  title: t("preChart.card.title"),
                  explain: t("preChart.card.draftExplain"),
                }
              : !hasMainRecording(file)
                ? {
                    title: t("preChart.card.title"),
                    explain: t("preChart.card.mergedExplain"),
                  }
                : undefined
          }
          reviewHint={
            isPreChartDraft(file.status)
              ? t("preChart.reviewHint")
              : !hasMainRecording(file)
                ? // A pre-charted visit: the recording's own values and the
                  // pre-chart values it changed are marked.
                  t("preChart.mergedReviewHint")
                : undefined
          }
          sourcesVisitPublicId={
            // A pre-chart draft has no transcript to point to.
            AppConfig.enableNoteSources && !isPreChartDraft(file.status)
              ? file.public_id
              : undefined
          }
          tools={tools}
          onEditingChange={onEditingChange}
          onOpenAssistant={onOpenAssistant}
          onReviewRequestTaken={onReviewRequestTaken}
          onSave={actions.save}
        />
      )}
    </WithAllergyFlag>
  );
};

export const MainDocument = (
  props: DocumentProps & MainReviewProps & { file: FileData },
): JSX.Element => {
  const stream = useNoteGenerationStream(props.file);
  if (isNoteGenerating(props.file) && AppConfig.enableAppRedesign) {
    return (
      <div className={cn("py-1 font-v3", NOTE_READING_CLASS)}>
        <NoteGeneratingBody file={props.file} stream={stream} />
      </div>
    );
  }
  if (isNoteGenerating(props.file)) {
    return (
      <div>
        <div className="border-b px-4 pb-3 sm:px-6">{props.heading}</div>
        <div
          className={cn(
            "px-4 py-5 sm:px-6 sm:py-6 lg:px-8",
            NOTE_READING_CLASS,
          )}
        >
          <NoteGeneratingBody file={props.file} stream={stream} />
        </div>
      </div>
    );
  }
  return <MainDocumentReady {...props} />;
};

export const PATIENT_SUMMARY_TEMPLATE = "Patient Summary";

// A patient summary can be written again at another reading level. The new
// summary opens next to this one, which stays as it is.
const useRegenerateSummary = (
  note: NoteTemplate,
  blocked: boolean,
  onRegenerate?: (level: ReadingLevel, language: SummaryLanguage) => void,
) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (note.template !== PATIENT_SUMMARY_TEMPLATE || !onRegenerate) return null;
  const label = t("visit.workspace.readingLevel.regenerateAction");
  const start = (): void => {
    setOpen(true);
  };
  return {
    tools: (
      <Button
        disabled={blocked}
        type="button"
        variant="outline"
        onClick={start}
      >
        <RefreshCcw aria-hidden />
        {label}
      </Button>
    ),
    menuItems: (
      <DropdownMenuItem className="gap-3" disabled={blocked} onSelect={start}>
        <RefreshCcw aria-hidden />
        {label}
      </DropdownMenuItem>
    ),
    dialog: (
      <ReadingLevelDialog
        mode="regenerate"
        open={open}
        onConfirm={onRegenerate}
        onOpenChange={setOpen}
      />
    ),
  };
};

export const ExtraDocument = ({
  note,
  file,
  heading,
  name,
  noteGenerationBlocked = false,
  onEditingChange,
  onOpenAssistant,
  onRegeneratePatientSummary,
}: DocumentProps & {
  note: NoteTemplate;
  file: FileData;
  noteGenerationBlocked?: boolean;
  onRegeneratePatientSummary?: (
    level: ReadingLevel,
    language: SummaryLanguage,
  ) => void;
}): JSX.Element => {
  const { save, saving } = useVisitNoteSave(note.visitPublicId, note.public_id);
  const regenerate = useRegenerateSummary(
    note,
    noteGenerationBlocked,
    onRegeneratePatientSummary,
  );
  return (
    <>
      <NoteDocument
        exportAction={
          <ExportAction
            doc={note}
            file={file}
            name={name ?? documentTemplateName(note) ?? ""}
            result={note.result}
          />
        }
        feedbackVisitPublicId={note.visitPublicId ?? file.public_id}
        heading={heading}
        menuItems={regenerate?.menuItems}
        result={note.result}
        saving={saving}
        tools={regenerate?.tools}
        onEditingChange={onEditingChange}
        onOpenAssistant={onOpenAssistant}
        onSave={save}
      />
      {regenerate?.dialog}
    </>
  );
};

interface Props extends DocumentProps, MainReviewProps {
  file: FileData;
  notes: NoteTemplate[];
  activeKey: string;
  onActiveKeyChange: (key: string) => void;
  noteGenerationBlocked?: boolean;
  // Writes a new patient summary at the chosen reading level.
  onRegeneratePatientSummary?: (
    level: ReadingLevel,
    language: SummaryLanguage,
  ) => void;
}

// The visit's documents (the clinical note first, then referral letters,
// patient summaries and other notes) in one card, one open at a time.
export const WorkspaceDocuments = ({
  file,
  notes,
  activeKey,
  onActiveKeyChange,
  noteGenerationBlocked,
  onEditingChange,
  onOpenAssistant,
  onRegeneratePatientSummary,
  reviewRequest,
  onReviewRequestTaken,
  copyInHeader,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const mainDocument = {
    key: MAIN_DOCUMENT_KEY,
    name: documentTemplateName(file) || t("visit.workspace.untitledNote"),
    meta: documentMeta(file),
  };
  const documents = [
    mainDocument,
    ...notes.map((note) => ({
      key: noteDocumentKey(note),
      name: documentTemplateName(note) || t("visit.workspace.untitledNote"),
      meta: documentMeta(note),
    })),
  ];
  const active = documents.find((doc) => doc.key === activeKey) ?? mainDocument;
  // v3.2: the note sits on the panel (no card), the documents as chips; the
  // clinical note's name and options are in the visit header's meta line.
  const v3 = AppConfig.enableAppRedesign;

  return (
    <TabsPrimitive.Root
      className={
        v3
          ? "min-w-0"
          : "overflow-hidden border-y bg-card max-sm:-mx-4 sm:rounded-xl sm:border-x"
      }
      value={active.key}
      onValueChange={onActiveKeyChange}
    >
      <div className={v3 ? undefined : "px-4 pt-4 sm:px-6 sm:pt-5"}>
        {documents.length > 1 && (
          <TabsPrimitive.List
            aria-label={t("visit.workspace.documents")}
            className={cn(
              "-mx-1 flex gap-1 overflow-x-auto px-1 pb-1 scrollbar-none",
              v3 ? "mb-5 gap-1.5" : "mb-3",
            )}
          >
            {documents.map((doc) => (
              <TabsPrimitive.Trigger
                key={doc.key}
                className={
                  v3
                    ? v3ChipVariants()
                    : "h-11 shrink-0 rounded-full border px-4 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:text-foreground lg:h-9"
                }
                value={doc.key}
              >
                {doc.name}
              </TabsPrimitive.Trigger>
            ))}
          </TabsPrimitive.List>
        )}
      </div>
      <TabsPrimitive.Content
        className="focus-visible:outline-hidden"
        value={MAIN_DOCUMENT_KEY}
      >
        <MainDocument
          file={file}
          name={mainDocument.name}
          heading={
            v3 ? undefined : (
              <DocumentHeading
                meta={mainDocument.meta}
                name={mainDocument.name}
              />
            )
          }
          copyInHeader={copyInHeader}
          reviewRequest={reviewRequest}
          onEditingChange={onEditingChange}
          onOpenAssistant={onOpenAssistant}
          onReviewRequestTaken={onReviewRequestTaken}
        />
      </TabsPrimitive.Content>
      {notes.map((note) => (
        <TabsPrimitive.Content
          key={note.public_id}
          className="focus-visible:outline-hidden"
          value={noteDocumentKey(note)}
        >
          <ExtraDocument
            file={file}
            name={
              documentTemplateName(note) || t("visit.workspace.untitledNote")
            }
            heading={
              <DocumentHeading
                badge={<SummaryLanguageLabel note={note} />}
                meta={documentMeta(note)}
                name={
                  documentTemplateName(note) ||
                  t("visit.workspace.untitledNote")
                }
              />
            }
            note={note}
            noteGenerationBlocked={noteGenerationBlocked}
            onEditingChange={onEditingChange}
            onOpenAssistant={onOpenAssistant}
            onRegeneratePatientSummary={onRegeneratePatientSummary}
          />
        </TabsPrimitive.Content>
      ))}
    </TabsPrimitive.Root>
  );
};
